'use client'

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useRouter } from '@/shared/navigation'
import { isValidActivationCode, normalizeActivationCodeInput } from '@/features/auth/activationCode'
import {
  activateDelmanDevice,
  lookupDelmanActivationCode,
  type DelmanActivationPreview,
} from '@/features/auth/api/activateDelmanDevice'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { DelmanAuthFlowStepper } from '@/features/delman/components/DelmanAuthFlowStepper'
import {
  clearDelmanDevice,
  getDelmanDevice,
  hasDelmanDevice,
  setDelmanDevice,
} from '@/shared/auth/delmanDeviceStorage'
import { clearDelmanSession } from '@/shared/auth/delmanSessionStorage'
import { clearDeviceUid, getDeviceOs, getOrCreateDeviceUid } from '@/shared/auth/deviceUid'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

type SubmitState = 'idle' | 'verifying' | 'success' | 'error'
type PreviewState = 'idle' | 'loading' | 'ready' | 'error'

function PreviewSkeleton() {
  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container p-4 space-y-3 animate-pulse">
      <div className="h-3 w-32 bg-surface-variant rounded" />
      <div className="h-4 w-full bg-surface-variant rounded" />
      <div className="h-4 w-3/4 bg-surface-variant rounded" />
    </div>
  )
}

function PreviewConfirmCard({ preview }: { preview: DelmanActivationPreview }) {
  const rows = [
    { icon: 'person', label: 'Delman', value: preview.delman_name },
    { icon: 'skillet', label: 'Dapur', value: preview.kitchen_name },
    { icon: 'smartphone', label: 'Perangkat', value: preview.device_name },
  ] as const

  return (
    <div className="rounded-xl border-2 border-status-success/40 bg-status-success/5 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="material-symbols-outlined text-status-success text-xl">verified</span>
        <p className="text-body-sm font-semibold text-status-success">{delmanTerms.activation.codeValid}</p>
      </div>
      <p className="text-label-md text-on-surface-variant uppercase tracking-wide">
        {delmanTerms.activation.previewTitle}
      </p>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.label} className="flex items-center gap-3 rounded-lg bg-surface-container-lowest/80 px-3 py-2.5 min-h-[44px]">
            <span className="material-symbols-outlined text-primary shrink-0">{row.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-on-surface-variant uppercase tracking-wide">{row.label}</p>
              <p className="text-body-sm font-semibold text-on-surface truncate">{row.value}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function DelmanActivatePage() {
  const router = useRouter()
  const codeInputRef = useRef<HTMLInputElement>(null)
  const hydrated = useClientHydrated()
  const existingDevice = hydrated ? getDelmanDevice() : null

  const [code, setCode] = useState('')
  const [preview, setPreview] = useState<DelmanActivationPreview | null>(null)
  const [previewState, setPreviewState] = useState<PreviewState>('idle')
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showRebindForm, setShowRebindForm] = useState(true)

  useEffect(() => {
    if (!hydrated) return
    setShowRebindForm(!hasDelmanDevice())
  }, [hydrated])

  const isVerifying = submitState === 'verifying'
  const isSuccess = submitState === 'success'
  const isDisabled = isVerifying || isSuccess
  const canActivate = previewState === 'ready' && preview !== null && !isDisabled
  const codeComplete = isValidActivationCode(code)

  useEffect(() => {
    if (showRebindForm) {
      codeInputRef.current?.focus()
    }
  }, [showRebindForm])

  useEffect(() => {
    if (!codeComplete) {
      setPreview(null)
      setPreviewState('idle')
      setPreviewError(null)
      return
    }

    setPreviewState('loading')
    setPreviewError(null)

    const timer = window.setTimeout(() => {
      lookupDelmanActivationCode(code)
        .then((result) => {
          if (result.device_type !== 'delman_mobile') {
            throw new Error(delmanTerms.activation.wrongDeviceType)
          }
          setPreview(result)
          setPreviewState('ready')
        })
        .catch((err) => {
          setPreview(null)
          setPreviewState('error')
          setPreviewError(err instanceof Error ? err.message : 'Kode tidak dikenali.')
        })
    }, 400)

    return () => window.clearTimeout(timer)
  }, [code, codeComplete])

  function handleRebind() {
    clearDelmanSession()
    clearDelmanDevice()
    clearDeviceUid()
    setShowRebindForm(true)
    setCode('')
    setPreview(null)
    setPreviewState('idle')
    setSubmitState('idle')
    setSubmitError(null)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (!canActivate) return

    setSubmitState('verifying')
    try {
      const binding = await activateDelmanDevice({
        activation_code: code,
        device_uid: getOrCreateDeviceUid(),
        os: getDeviceOs(),
      })
      setDelmanDevice(binding)
      clearDelmanSession()
      setSubmitState('success')
      setTimeout(() => router.replace('/delman/login'), 700)
    } catch (err) {
      setSubmitState('error')
      setSubmitError(err instanceof Error ? err.message : 'Aktivasi gagal. Coba lagi.')
    }
  }

  if (hydrated && existingDevice && !showRebindForm) {
    return (
      <main className="w-full max-w-[480px]">
        <div className="bg-surface-card rounded-xl border border-outline-variant shadow-sm overflow-hidden">
          <div className="bg-surface pt-8 pb-6 px-gutter-tablet flex flex-col items-center border-b border-surface-variant">
            <div className="w-16 h-16 rounded-full bg-status-success/15 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-[32px] text-status-success icon-fill">
              check_circle
            </span>
          </div>
          <div className="w-full mb-4">
            <DelmanAuthFlowStepper activeStep={2} />
          </div>
          <h1 className="text-headline-md text-primary text-center">{delmanTerms.activation.alreadyBoundTitle}</h1>
          </div>

          <div className="p-gutter-tablet space-y-4">
            <div className="rounded-xl bg-surface-container p-4 space-y-2 text-body-sm">
              <p>
                <span className="text-on-surface-variant">Delman</span>{' '}
                <strong className="text-on-surface">{existingDevice.delmanName}</strong>
              </p>
              <p>
                <span className="text-on-surface-variant">Dapur</span>{' '}
                <strong className="text-on-surface">{existingDevice.kitchenName}</strong>
              </p>
              <p>
                <span className="text-on-surface-variant">Perangkat</span>{' '}
                <strong className="text-on-surface">{existingDevice.deviceName}</strong>
              </p>
            </div>

            <Link href="/delman/login" className="btn-primary block text-center min-h-[52px] leading-[52px]">
              {delmanTerms.activation.alreadyBoundAction}
            </Link>
            <button type="button" className="btn-outline w-full min-h-[48px]" onClick={handleRebind}>
              {delmanTerms.activation.rebindAction}
            </button>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="w-full max-w-[480px]">
      <div className="bg-surface-card rounded-xl border border-outline-variant shadow-sm overflow-hidden flex flex-col">
        <div className="bg-surface pt-8 pb-6 px-gutter-tablet flex flex-col items-center border-b border-surface-variant">
          <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center mb-4 text-on-primary-container shadow-sm border border-outline-variant/30">
            <span className="material-symbols-outlined text-[32px] icon-fill">local_shipping</span>
          </div>
          <div className="w-full mb-4">
            <DelmanAuthFlowStepper activeStep={1} />
          </div>
          <h1 className="text-headline-md text-primary mb-1 text-center">{delmanTerms.appName}</h1>
          <h2 className="text-headline-lg-mobile text-on-surface text-center">{delmanTerms.activation.title}</h2>
        </div>

        <div className="p-gutter-tablet flex flex-col gap-6">
          <p className="text-body-sm text-on-surface-variant text-center leading-relaxed">
            {delmanTerms.activation.subtitle}
          </p>

          <div className="flex items-start gap-2 rounded-lg bg-surface-container px-3 py-2.5 text-body-sm text-on-surface-variant">
            <span className="material-symbols-outlined text-base text-primary shrink-0 mt-0.5">info</span>
            <span>{delmanTerms.activation.hint}</span>
          </div>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-3">
              <label htmlFor="delman_activation_code" className="field-label flex items-center justify-between">
                <span>Kode aktivasi</span>
                {previewState === 'loading' && (
                  <span className="material-symbols-outlined text-base text-primary animate-spin">progress_activity</span>
                )}
                {previewState === 'ready' && (
                  <span className="material-symbols-outlined text-base text-status-success">check_circle</span>
                )}
                {previewState === 'error' && (
                  <span className="material-symbols-outlined text-base text-status-danger">error</span>
                )}
              </label>

              <input
                ref={codeInputRef}
                id="delman_activation_code"
                name="activation_code"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                autoFocus
                spellCheck={false}
                disabled={isDisabled}
                value={code}
                onChange={(e) => {
                  setCode(normalizeActivationCodeInput(e.target.value))
                  setSubmitState('idle')
                  setSubmitError(null)
                }}
                placeholder="7X9K2M"
                maxLength={6}
                className="input-code"
                aria-describedby="delman_code_helper"
                aria-invalid={previewState === 'error'}
              />

              <p id="delman_code_helper" className="text-label-md text-on-surface-variant text-center">
                {!codeComplete
                  ? delmanTerms.activation.codeHelper
                  : previewState === 'loading'
                    ? 'Memverifikasi kode…'
                    : previewState === 'ready'
                      ? 'Periksa data binding di bawah sebelum aktivasi.'
                      : ''}
              </p>
              {previewState === 'error' && previewError && (
                <p className="text-body-sm text-status-danger text-center" role="alert">
                  {previewError}
                </p>
              )}
            </div>

            {codeComplete && previewState === 'loading' && <PreviewSkeleton />}

            {previewState === 'ready' && preview && <PreviewConfirmCard preview={preview} />}

            {submitError && (
              <div role="alert" className="alert-error">
                <span className="material-symbols-outlined text-base shrink-0">error</span>
                <span>{submitError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!canActivate}
              className={`btn-cta btn-tactile ${isSuccess ? 'is-success' : ''}`}
            >
              {isVerifying && (
                <>
                  <span className="material-symbols-outlined animate-spin">progress_activity</span>
                  Memverifikasi…
                </>
              )}
              {isSuccess && (
                <>
                  <span className="material-symbols-outlined">check_circle</span>
                  Perangkat teraktivasi
                </>
              )}
              {!isVerifying && !isSuccess && (
                <>
                  Aktivasi & lanjut PIN
                  <span className="material-symbols-outlined font-bold">arrow_forward</span>
                </>
              )}
            </button>
          </form>
        </div>

        <div className="bg-surface-container py-4 px-gutter-tablet text-center border-t border-surface-variant">
          <p className="text-label-md text-on-surface-variant flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-sm">support_agent</span>
            {delmanTerms.activation.footer}
          </p>
        </div>
      </div>
    </main>
  )
}
